// NOVA-POSHTA-117A-R6 / CUSTOMER-R4.5 — warehouse autocomplete (city-scoped, stale-safe).
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  listNovaPoshtaWarehouses,
  type WarehouseOption,
} from "./nova-poshta-reference-client.js";
import {
  applyReplaceOnFirstInput,
  emitDeliveryAnalytics,
  extractWarehouseNumberQuery,
  handleFirstKeyReplaceSelection,
  NP_COMBOBOX_DEBOUNCE_MS,
  npDirectoryErrorCopy,
  npWarehouseEmptyCopy,
  npWarehouseLoadingCopy,
  shouldSearchWarehouses,
  warehouseApiQuery,
} from "./nova-poshta-combobox-replace.js";

export type NovaPoshtaWarehouseMode = "branch" | "parcel_locker";

function warehouseDisplayLabel(w: WarehouseOption): string {
  if (w.number && w.shortAddress) return `№${w.number}: ${w.shortAddress}`;
  if (w.number) return `№${w.number}`;
  return w.label;
}

function comboboxFocusSelectAll(el: HTMLInputElement | null): void {
  requestAnimationFrame(() => {
    if (!el) return;
    try {
      el.select();
    } catch {
      /* ignore */
    }
  });
}

function rankWarehouseCards(items: WarehouseOption[], query: string): WarehouseOption[] {
  const warehouseId = extractWarehouseNumberQuery(query);
  const q = warehouseApiQuery(query).toLowerCase();
  if (!q) return items;
  return [...items].sort((a, b) => {
    const score = (w: WarehouseOption) => {
      if (warehouseId && w.number === warehouseId) return 0;
      if (warehouseId && w.number?.startsWith(warehouseId)) return 1;
      const hay = `${w.label} ${w.shortAddress}`.toLowerCase();
      if (warehouseId && hay.includes(`№${warehouseId}`)) return 2;
      if (hay.includes(q)) return 3;
      return 4;
    };
    return score(a) - score(b);
  });
}

export function NovaPoshtaWarehouseCombobox({
  lang,
  disabled,
  settlementRef,
  mode,
  value,
  onChange,
  onProviderError,
  onRetryRef,
}: {
  lang: StoreV2Lang;
  disabled?: boolean;
  settlementRef: string | null;
  mode: NovaPoshtaWarehouseMode;
  value: WarehouseOption | null;
  onChange: (next: WarehouseOption | null) => void;
  onProviderError: (message: string | null, meta?: { rateLimited?: boolean; retryAfterSec?: number | null }) => void;
  onRetryRef?: { current: (() => void) | null };
}) {
  const locale = lang === "en" ? "en" : "uk";
  const copyLang = lang === "en" ? "en" : "uk";
  const [query, setQuery] = useState(() => (value ? warehouseDisplayLabel(value) : ""));
  const [items, setItems] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const replaceArmedRef = useRef(false);
  const searchStartedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestGenRef = useRef(0);

  const selectedLabel = value ? warehouseDisplayLabel(value) : null;

  useEffect(() => {
    if (value) setQuery(warehouseDisplayLabel(value));
    else setQuery("");
    setItems([]);
    setLoadError(false);
    // CUSTOMER-R4.15-C — city/mode change clears stale provider error immediately.
    onProviderError(null);
    replaceArmedRef.current = false;
    searchStartedRef.current = false;
    requestGenRef.current += 1;
    abortRef.current?.abort();
  }, [settlementRef, mode, value?.warehouseRef, onProviderError]);

  const load = useCallback(
    (pageNum: number, append: boolean) => {
      if (disabled || !settlementRef) return;
      if (!shouldSearchWarehouses(query)) {
        // CUSTOMER-R4.15-C — empty/debounce query is neutral, not a provider failure.
        setLoading(false);
        setLoadError(false);
        onProviderError(null);
        if (!append) setItems([]);
        return;
      }
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const gen = ++requestGenRef.current;
      setLoading(true);
      setLoadError(false);
      if (!searchStartedRef.current) {
        searchStartedRef.current = true;
        emitDeliveryAnalytics("delivery.warehouse_search_started", { deliveryType: mode });
      }
      const kind = mode === "parcel_locker" ? "parcel_locker" : ("all_branches" as const);
      const q = warehouseApiQuery(query);
      void listNovaPoshtaWarehouses({
        settlementRef,
        kind,
        locale,
        page: pageNum,
        limit: 30,
        signal: ac.signal,
        q,
      }).then((res) => {
        if (ac.signal.aborted || gen !== requestGenRef.current || res.aborted) return;
        setLoading(false);
        if (!res.ok) {
          emitDeliveryAnalytics("delivery.directory_error", { deliveryType: mode });
          setLoadError(true);
          if (res.rateLimited) {
            onProviderError(npDirectoryErrorCopy(copyLang), {
              rateLimited: true,
              retryAfterSec: res.retryAfterSec ?? 5,
            });
          } else {
            onProviderError(npDirectoryErrorCopy(copyLang));
          }
          if (!append) setItems([]);
          return;
        }
        onProviderError(null);
        setLoadError(false);
        const filtered =
          mode === "parcel_locker"
            ? res.items.filter((w) => w.kind === "parcel_locker")
            : res.items.filter((w) => w.kind !== "parcel_locker");
        const ranked = rankWarehouseCards(filtered, query);
        setItems((prev) => (append ? [...prev, ...ranked] : ranked));
        setHasMore(res.items.length >= res.limit);
        setPage(pageNum);
        setActiveIndex(-1);
      });
    },
    [disabled, settlementRef, mode, query, locale, copyLang, onProviderError],
  );

  useEffect(() => {
    if (onRetryRef) onRetryRef.current = () => load(1, false);
  }, [load, onRetryRef]);

  useEffect(() => {
    if (disabled || !settlementRef) {
      setItems([]);
      setLoading(false);
      return;
    }
    if (value && query === selectedLabel) {
      return;
    }
    if (!shouldSearchWarehouses(query)) {
      setItems([]);
      setLoading(false);
      setLoadError(false);
      onProviderError(null);
      return;
    }
    const timer = window.setTimeout(() => {
      load(1, false);
    }, NP_COMBOBOX_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [disabled, settlementRef, mode, query, value, selectedLabel, load]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const clearSelection = () => onChange(null);

  const applyEdit = (nextRaw: string, insertedData?: string | null) => {
    const result = applyReplaceOnFirstInput({
      replaceArmed: replaceArmedRef.current,
      selectedLabel,
      currentQuery: query,
      nextValue: nextRaw.slice(0, 80),
      ...(insertedData !== undefined ? { insertedData } : {}),
    });
    replaceArmedRef.current = false;
    setQuery(result.nextQuery);
    if (result.shouldClearSelection && value) clearSelection();
  };

  const pick = (w: WarehouseOption) => {
    if (!w.warehouseRef || w.warehouseRef.length < 8) return;
    onChange(w);
    setQuery(warehouseDisplayLabel(w));
    setItems([]);
    setLoadError(false);
    replaceArmedRef.current = false;
    searchStartedRef.current = false;
    emitDeliveryAnalytics("delivery.warehouse_selected", { deliveryType: mode, hasResults: true });
  };

  const listId = "sv2-np-warehouse-listbox";
  const fieldDisabled = disabled || !settlementRef;
  const showList = !fieldDisabled && items.length > 0 && !(value && query === selectedLabel);
  const queryActive = shouldSearchWarehouses(query) && !(value && query === selectedLabel);
  const empty =
    !loading && !loadError && !!settlementRef && items.length === 0 && !value && queryActive;

  return (
    <div
      className="sv2-field sv2-np-combobox"
      data-testid="sv2-np-warehouse-combobox"
      data-replace-on-first-input="1"
      data-warehouse-mode={mode}
    >
      <label>
        {mode === "parcel_locker"
          ? lang === "en"
            ? "Parcel locker"
            : "Поштомат"
          : lang === "en"
            ? "Branch"
            : "Відділення"}{" "}
        *
      </label>
      {!settlementRef ? (
        <div className="sv2-muted sv2-np-hint" data-testid="sv2-np-warehouse-need-city">
          {lang === "en" ? "Select a city from the list first." : "Спочатку оберіть місто зі списку."}
        </div>
      ) : null}
      <div className="sv2-np-combobox-input-wrap">
        <input
          ref={inputRef}
          value={query}
          disabled={fieldDisabled}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          onFocus={() => {
            if (fieldDisabled) return;
            if (value) {
              replaceArmedRef.current = true;
              comboboxFocusSelectAll(inputRef.current);
            }
            // Do not search on every focus — debounce effect handles typing; selection stays put.
          }}
          onBeforeInput={(e) => {
            // COMMERCE-LAUNCH-168D1 — never interrupt active IME composition (SwiftKey/Android).
            if ((e.nativeEvent as InputEvent).isComposing) return;
            if (!replaceArmedRef.current || !selectedLabel || query !== selectedLabel) return;
            const data = (e.nativeEvent as InputEvent).data;
            if (!data) return;
            e.preventDefault();
            applyEdit(data, data);
          }}
          onChange={(e) => {
            applyEdit(e.target.value);
          }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (replaceArmedRef.current && selectedLabel && query === selectedLabel) {
              const hk = handleFirstKeyReplaceSelection(e.key, selectedLabel, query, {
                ctrlKey: e.ctrlKey,
                metaKey: e.metaKey,
                altKey: e.altKey,
              });
              if (hk.handled) {
                e.preventDefault();
                replaceArmedRef.current = false;
                clearSelection();
                setQuery(hk.nextQuery);
                return;
              }
            }
            if (e.key === "Escape") {
              setItems([]);
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
              return;
            }
            if (e.key === "Enter" && activeIndex >= 0 && items[activeIndex]) {
              e.preventDefault();
              pick(items[activeIndex]!);
            }
          }}
          placeholder={lang === "en" ? "Branch number or address" : "Номер або адреса відділення"}
          autoComplete="off"
          inputMode="search"
        />
        {value || query ? (
          <button
            type="button"
            className="sv2-np-combobox-clear"
            aria-label={lang === "en" ? "Clear location" : "Очистити відділення"}
            data-testid="sv2-np-warehouse-clear"
            onClick={() => {
              clearSelection();
              setQuery("");
              setItems([]);
              setLoadError(false);
              replaceArmedRef.current = false;
              searchStartedRef.current = false;
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {loading ? (
        <div className="sv2-muted sv2-np-hint" data-testid="sv2-np-warehouse-loading">
          {npWarehouseLoadingCopy(copyLang)}
        </div>
      ) : null}
      {loadError && !loading ? (
        <div className="sv2-np-empty" data-testid="sv2-np-warehouse-error">
          <div>{npDirectoryErrorCopy(copyLang)}</div>
          <button
            type="button"
            className="sv2-np-retry"
            data-testid="sv2-np-warehouse-retry"
            onClick={() => load(1, false)}
          >
            {lang === "en" ? "Try again" : "Спробувати ще"}
          </button>
        </div>
      ) : null}
      {empty ? (
        <div
          className="sv2-np-empty"
          data-testid={mode === "parcel_locker" ? "sv2-np-locker-empty" : "sv2-np-branch-empty"}
        >
          {npWarehouseEmptyCopy(copyLang, mode === "parcel_locker" ? "parcel_locker" : "branch")}
        </div>
      ) : null}
      {showList ? (
        <ul className="sv2-np-list sv2-np-list-cards" role="listbox" id={listId} data-testid="sv2-np-warehouse-list">
          {items.map((w, idx) => (
            <li key={w.warehouseRef} role="option" aria-selected={idx === activeIndex}>
              <button
                type="button"
                className={`sv2-np-option sv2-np-option-card${idx === activeIndex ? " is-active" : ""}`}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => pick(w)}
              >
                <span className="sv2-np-option-title">
                  {w.kind === "parcel_locker"
                    ? `📦 ${lang === "en" ? "Postal machine" : "Поштомат"} ${w.number ? `№${w.number}` : ""}`.trim()
                    : `🏢 ${w.number ? `№${w.number}` : w.label}`}
                </span>
                <span className="sv2-np-option-meta">{w.shortAddress || w.label}</span>
                {w.maxWeightKg != null ? (
                  <span className="sv2-np-option-weight">
                    {lang === "en" ? `up to ${w.maxWeightKg} kg` : `до ${w.maxWeightKg} кг`}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {hasMore && !loading && showList ? (
        <button
          type="button"
          className="sv2-np-load-more"
          data-testid="sv2-np-load-more"
          onClick={() => load(page + 1, true)}
        >
          {lang === "en" ? "Load more" : "Завантажити ще"}
        </button>
      ) : null}
    </div>
  );
}
